import { ExpressionKeyField, getExpressionKeyTitle } from "../constants";
import { validateCharPolicy } from "./charPolicies";
import {
  isValidBotScore,
  validationResult,
} from "./patterns";
import {
  validateAsn,
  validateCity,
  validateCountryCode,
  validateIpOrCidrOrRange,
  validateJa3,
  validateJa4,
} from "./yupCommonFields";

const VALID_METHODS = new Set(["GET", "POST", "HEAD", "PUT", "DELETE", "PATCH", "TRACE", "CONNECT"]);

const NONE = "none";

function expressionFieldLabel(key) {
  return getExpressionKeyTitle(key);
}

function requiredFieldMsg(key) {
  switch (key) {
    case ExpressionKeyField.SOURCE_IP:
      return "Source IP is required";
    case ExpressionKeyField.HOST_NAME:
      return "Host name is required";
    case ExpressionKeyField.METHOD:
      return "HTTP method is required";
    case ExpressionKeyField.USER_AGENT:
      return "User agent is required";
    default:
      return `${expressionFieldLabel(key)} is required`;
  }
}

function maxLengthFieldMsg(key, max) {
  switch (key) {
    case ExpressionKeyField.HOST_NAME:
      return "Host name must be 253 characters or fewer";
    case ExpressionKeyField.HEADER:
      return "Header value must be 1024 characters or fewer";
    case ExpressionKeyField.URI:
      return "URI must be 1024 characters or fewer";
    case ExpressionKeyField.QUERY:
      return "Query string must be 1024 characters or fewer";
    case ExpressionKeyField.COOKIE:
      return "Cookie must be 1024 characters or fewer";
    case ExpressionKeyField.REFERER:
      return "Referer must be 1024 characters or fewer";
    case ExpressionKeyField.USER_AGENT:
      return "User agent must be 1024 characters or fewer";
    default:
      return `${expressionFieldLabel(key)} must be ${max} characters or fewer`;
  }
}

export const EXPRESSION_VALUE_HINTS = {
  [ExpressionKeyField.SOURCE_IP]: "IPv4/IPv6 address, CIDR, or range (e.g. 10.0.0.0/24)",
  [ExpressionKeyField.HOST_NAME]: "HTTP Host header value, e.g. api.example.com (max 253 characters)",
  [ExpressionKeyField.METHOD]: "Select an HTTP method",
  [ExpressionKeyField.COUNTRY]: "ISO country code from the list",
  [ExpressionKeyField.CITY_NAME]: "City name, 1–128 characters",
  [ExpressionKeyField.AS_NUMBER]: "Autonomous System Number, e.g. 64512",
  [ExpressionKeyField.BOT_SCORE]: "Integer from 0 to 100",
  [ExpressionKeyField.JA3_FINGERPRINT]: "TLS JA3 fingerprint (32 hex chars)",
  [ExpressionKeyField.JA4_FINGERPRINT]: "TLS JA4 fingerprint (1–64 alphanumeric/underscore)",
  [ExpressionKeyField.HEADER]: "Header value (max 1024 characters)",
  [ExpressionKeyField.URI]: "URI path (max 1024 characters)",
  [ExpressionKeyField.QUERY]: "Query string value (max 1024 characters)",
  [ExpressionKeyField.COOKIE]: "Cookie value (max 1024 characters)",
  [ExpressionKeyField.REFERER]: "Referer value (max 1024 characters)",
  [ExpressionKeyField.USER_AGENT]: "User-Agent string (max 1024 characters)",
};

export function getExpressionValueHint(keyStr) {
  if (!keyStr || keyStr === NONE || keyStr === ExpressionKeyField.NONE) return "";
  return EXPRESSION_VALUE_HINTS[keyStr] || "";
}

export function isExpressionRowStarted(andCond) {
  const key = andCond?.key;
  return Boolean(key && key !== NONE && key !== ExpressionKeyField.NONE);
}

/** Show inline cell error when touched, submit attempted, row started, or value entered. */
export function shouldShowExpressionCellError(
  errorMsg,
  { touched = false, submitAttempted = false, rowStarted = false, valueEntered = false } = {},
) {
  return Boolean(errorMsg && (touched || submitAttempted || rowStarted || valueEntered));
}

/** Returns the error message when visible, else false (BM-style showErr). */
export function resolveVisibleCellError(errorMsg, options) {
  if (!errorMsg) return false;
  return shouldShowExpressionCellError(errorMsg, options) ? errorMsg : false;
}

export const RATE_LIMIT_CHAR_HINTS = {
  headers: "Header name (max 128 characters)",
  cookie: "Cookie name (max 128 characters)",
  query: "Query parameter name (max 128 characters)",
};

/** Pattern/length validation for non-empty values; no operator or required checks. */
export function validateExpressionValueFormat(keyStr, valueStr, { headerName } = {}) {
  const key = keyStr === undefined || keyStr === null ? NONE : keyStr;
  const value = valueStr === undefined || valueStr === null ? "" : String(valueStr).trim();

  if (!value || value === "none") return validationResult(true);
  if (key === NONE || key === ExpressionKeyField.NONE) return validationResult(true);

  switch (key) {
    case ExpressionKeyField.SOURCE_IP:
      return validateIpOrCidrOrRange(value, { required: false });

    case ExpressionKeyField.HOST_NAME:
      if (value.length > 253) return validationResult(false, maxLengthFieldMsg(key, 253));
      return validationResult(true);

    case ExpressionKeyField.METHOD:
      if (!VALID_METHODS.has(value.toUpperCase())) {
        return validationResult(false, "Select a valid HTTP method");
      }
      return validationResult(true);

    case ExpressionKeyField.COUNTRY:
      return validateCountryCode(value, { required: false });

    case ExpressionKeyField.CITY_NAME:
      return validateCity(value, { required: false });

    case ExpressionKeyField.AS_NUMBER:
      return validateAsn(value, { required: false });

    case ExpressionKeyField.BOT_SCORE:
      if (!isValidBotScore(value)) {
        return validationResult(false, "Bot score must be an integer from 0 to 100");
      }
      return validationResult(true);

    case ExpressionKeyField.JA3_FINGERPRINT:
      return validateJa3(value, { required: false });

    case ExpressionKeyField.JA4_FINGERPRINT:
      return validateJa4(value, { required: false });

    case ExpressionKeyField.HEADER:
      if (value.length > 1024) return validationResult(false, maxLengthFieldMsg(key, 1024));
      return validationResult(true);

    case ExpressionKeyField.URI:
    case ExpressionKeyField.QUERY:
    case ExpressionKeyField.COOKIE:
    case ExpressionKeyField.REFERER:
    case ExpressionKeyField.USER_AGENT:
      if (value.length > 1024) return validationResult(false, maxLengthFieldMsg(key, 1024));
      return validationResult(true);

    default:
      return validationResult(true);
  }
}

/** @returns {{ valid: boolean, message?: string }} */
export function validateExpressionValue(keyStr, condition, valueStr, { headerName } = {}) {
  const key = keyStr === undefined || keyStr === null ? NONE : keyStr;
  const cond = condition === undefined || condition === null ? NONE : condition;
  const value = valueStr === undefined || valueStr === null ? "" : String(valueStr).trim();

  if (key === NONE || key === ExpressionKeyField.NONE) {
    return validationResult(false, "Select a field");
  }
  if (cond === NONE || cond === "none") {
    return validationResult(false, "Select an operator");
  }

  switch (key) {
    case ExpressionKeyField.SOURCE_IP:
      return validateIpOrCidrOrRange(value);

    case ExpressionKeyField.HOST_NAME:
      if (!value) return validationResult(false, requiredFieldMsg(key));
      if (value.length > 253) return validationResult(false, maxLengthFieldMsg(key, 253));
      return validationResult(true);

    case ExpressionKeyField.METHOD:
      if (!value || value === "none") return validationResult(false, requiredFieldMsg(key));
      if (!VALID_METHODS.has(value.toUpperCase())) {
        return validationResult(false, "Select a valid HTTP method");
      }
      return validationResult(true);

    case ExpressionKeyField.COUNTRY:
      return validateCountryCode(value);

    case ExpressionKeyField.CITY_NAME:
      return validateCity(value);

    case ExpressionKeyField.AS_NUMBER:
      return validateAsn(value);

    case ExpressionKeyField.BOT_SCORE:
      if (value === "") return validationResult(false, "Bot score is required");
      if (!isValidBotScore(value)) {
        return validationResult(false, "Bot score must be an integer from 0 to 100");
      }
      return validationResult(true);

    case ExpressionKeyField.JA3_FINGERPRINT:
      return validateJa3(value);

    case ExpressionKeyField.JA4_FINGERPRINT:
      return validateJa4(value);

    case ExpressionKeyField.HEADER:
      if (!String(headerName ?? "").trim() && !value) {
        return validationResult(false, "Header name or value is required");
      }
      if (value.length > 1024) return validationResult(false, maxLengthFieldMsg(key, 1024));
      return validationResult(true);

    case ExpressionKeyField.URI:
    case ExpressionKeyField.QUERY:
    case ExpressionKeyField.COOKIE:
    case ExpressionKeyField.REFERER:
    case ExpressionKeyField.USER_AGENT:
      if (!value) return validationResult(false, requiredFieldMsg(key));
      if (value.length > 1024) return validationResult(false, maxLengthFieldMsg(key, 1024));
      return validationResult(true);

    default:
      if (!value) return validationResult(false, requiredFieldMsg(key));
      return validationResult(true);
  }
}

function conditionCellStarted(andCond) {
  return (
    andCond.key !== NONE &&
    andCond.key !== ExpressionKeyField.NONE &&
    andCond.key !== undefined &&
    andCond.key !== null
  ) || (
    andCond.condition !== NONE &&
    andCond.condition !== "none" &&
    andCond.condition !== undefined &&
    andCond.condition !== null
  ) || String(andCond.value ?? "").trim() !== "";
}

/** Validate nested OR/AND condition matrix used by WAF/Firewall/Rate-limit modals */
export function validateExpressionConditions(conditions) {
  const errors = [];
  let anyStarted = false;

  (conditions || []).forEach((orCond, i) => {
    const orErrors = [];
    (orCond || []).forEach((andCond, j) => {
      const cell = { key: false, condition: false, value: false };
      const started = conditionCellStarted(andCond);
      if (started) anyStarted = true;

      if (!started) {
        orErrors.push(cell);
        return;
      }

      if (!andCond.key || andCond.key === NONE || andCond.key === ExpressionKeyField.NONE) {
        cell.key = "Select a field";
      }
      if (!andCond.condition || andCond.condition === NONE || andCond.condition === "none") {
        cell.condition = "Select an operator";
      }

      if (!cell.key) {
        const valueTrim = String(andCond.value ?? "").trim();
        const operatorSet =
          andCond.condition && andCond.condition !== NONE && andCond.condition !== "none";

        if (valueTrim) {
          const formatResult = validateExpressionValueFormat(andCond.key, andCond.value, {
            headerName: andCond.name,
          });
          if (!formatResult.valid) {
            cell.value = formatResult.message;
          }
        }

        if (!cell.value && operatorSet) {
          const valueResult = validateExpressionValue(andCond.key, andCond.condition, andCond.value, {
            headerName: andCond.name,
          });
          if (!valueResult.valid && valueResult.message !== "Select an operator") {
            cell.value = valueResult.message;
          }
        }
      }

      orErrors.push({
        key: cell.key || false,
        condition: cell.condition || false,
        value: cell.value || false,
      });
    });
    errors.push(orErrors);
  });

  const hasErrors = errors.some((or) => or.some((c) => c.key || c.condition || c.value));
  return { valid: !hasErrors && (anyStarted || (conditions || []).length === 0), errors, anyStarted };
}

export function validateExpressionConditionsRequired(conditions) {
  const { errors, anyStarted } = validateExpressionConditions(conditions);
  const hasErrors = errors.some((or) => or.some((c) => c.key || c.condition || c.value));
  if (!anyStarted) {
    const nextErrors = errors.map((orCond, i) =>
      (orCond || []).map((cell, j) => {
        if (i === 0 && j === 0) {
          return { key: "Select a field", condition: "Select an operator", value: false };
        }
        return cell;
      }),
    );
    return { valid: false, errors: nextErrors, message: "Add at least one match condition" };
  }
  return { valid: !hasErrors, errors };
}

const RATE_LIMIT_NAME_KEYS = new Set(["headers", "cookie", "query"]);

/** Validate rate-limit characteristics row (header/cookie/query name when key selected). */
export function validateRateLimitCharacteristic(key, value) {
  const k = key === undefined || key === null ? "none" : key;
  const v = String(value ?? "").trim();
  if (k === "none" || !k) {
    return validationResult(false, "Select a characteristic");
  }
  if (RATE_LIMIT_NAME_KEYS.has(k)) {
    if (!v) return validationResult(false, "Name is required for this characteristic");
    if (v.length > 128) return validationResult(false, "Name must be 128 characters or fewer");
    const policyResult = validateCharPolicy("shortName", v, { allowEmpty: false });
    if (!policyResult.valid) return policyResult;
  }
  return validationResult(true);
}

export function validateRateLimitCharacteristics(characteristics) {
  const errors = (characteristics || []).map((row) => {
    const started = row?.key && row.key !== "none";
    if (!started) return { key: false, value: false };
    const result = validateRateLimitCharacteristic(row.key, row.value);
    if (!result.valid) {
      if (RATE_LIMIT_NAME_KEYS.has(row.key)) {
        return { key: false, value: result.message };
      }
      return { key: result.message, value: false };
    }
    return { key: false, value: false };
  });
  const hasErrors = errors.some((e) => e.key || e.value);
  const anyStarted = (characteristics || []).some((r) => r?.key && r.key !== "none");
  if (!anyStarted && (characteristics || []).length > 0) {
    errors[0] = { key: "Select a characteristic", value: false };
    return { valid: false, errors, message: "Add at least one rate limit characteristic" };
  }
  return { valid: !hasErrors && anyStarted, errors };
}
